/**
 * 工单管理 API 服务层
 * 通过统一的 /web/agent/sync/task 入口调用
 * 参照 AI 智能看图模块（src/actions/aiTask.js + useAITaskEvent.tsx）
 */
import { syncTask } from '../../actions/aiTask'
import {
  mapTagValue,
  TAG_VALUE_API_MAP,
  OrderRecord,
  OrderDetail
} from './constants'

// ============================================================
// 文件上传（腾讯云 COS）
// ============================================================

import cos from '../../utils/cos'

declare const __API_SERVER_HOST__: string

// 媒体文件 base URL — file_url 无 https:// 前缀时拼接
const MEDIA_BASE_URL = 'https://net-cloud.xhey.top'

// ============================================================
// 通用响应解析
// ============================================================

/**
 * 解析 output_param（兼容 outputParam / output_param 两种 key，兼容字符串或对象）
 * 参照 useAITaskEvent.tsx 第 136-145 行
 */
function parseOutputParam<T = any>(outputParam: any): T | null {
  if (!outputParam) return null
  if (typeof outputParam === 'object') return outputParam as T
  try {
    return JSON.parse(outputParam) as T
  } catch (e) {
    console.warn('[工单API] 解析 outputParam 失败:', e)
    return null
  }
}

interface TaskApiResponse {
  success: boolean
  message?: string
  data: any
}

/**
 * 调用工单 API 的统一入口
 * @param dispatch Redux dispatch
 * @param route task_route 路由名
 * @param params input_param 参数对象
 */
async function callWorkOrderApi<T = any>(
  dispatch: (...args: any[]) => any,
  route: string,
  params: Record<string, any>
): Promise<T> {
  const payload = {
    taskName: 'work_order',
    taskRoute: route,
    inputParam: JSON.stringify(params)
  }

  // 调用 syncTask action（src/actions/aiTask.js 第 56 行）
  const result: any = await dispatch(syncTask(payload))

  // 从响应中提取 output_param（兼容两种 key）
  const rawOutput = result?.data?.outputParam || result?.data?.output_param
  const parsed = parseOutputParam<TaskApiResponse>(rawOutput)

  if (!parsed) {
    throw new Error('API 响应解析失败')
  }

  if (!parsed.success) {
    throw new Error(parsed.message || '操作失败')
  }

  return parsed.data as T
}

// ============================================================
// 列表 & 统计 & 详情
// ============================================================

/**
 * 点位列表 — task_route: checkpoint_list
 * @param machine_model  整机机型筛选（精确匹配）
 * @param machine_id     整机编号筛选（精确匹配）
 * @param process_name   装配工序筛选（精确匹配）
 * @param tag_value      分析结果筛选：'符合' | '不符合'（注意：传中文）
 * @param order_status   工单状态筛选：rectifying | reviewing | approved | rejected | appeal_reviewing
 * @param date_start     起始日期 YYYY-MM-DD（含）
 * @param date_end       截止日期 YYYY-MM-DD（含）
 * @param page           页码，默认 1
 * @param page_size      每页条数，默认 20，最大 100
 * @returns { checkpoints, total, page, page_size }
 */
export async function apiCheckpointList(
  dispatch: (...args: any[]) => any,
  params: {
    group_id: string
    status?: string
    machine_model?: string
    machine_id?: string
    process_name?: string
    tag_value?: string
    order_status?: string
    date_start?: string
    date_end?: string
    page?: number
    page_size?: number
  }
) {
  const data = await callWorkOrderApi<{
    checkpoints: any[]
    total: number
    page: number
    page_size: number
  }>(dispatch, 'checkpoint_list', params)

  // tag_value 中文→英文映射
  const checkpoints = (data.checkpoints || []).map((item: any) => ({
    ...item,
    tag_value: mapTagValue(item.tag_value)
  }))

  return { ...data, checkpoints }
}

/** 点位统计 */
export async function apiCheckpointStats(
  dispatch: (...args: any[]) => any,
  params: { group_id: string }
) {
  return callWorkOrderApi(dispatch, 'checkpoint_stats', params)
}

/** 点位详情 */
export async function apiCheckpointDetail(
  dispatch: (...args: any[]) => any,
  params: { checkpoint_key: string; user_id?: string }
) {
  const data = await callWorkOrderApi(dispatch, 'checkpoint_detail', params)

  // media_list: photo_file → file_url 兼容 + 生成 id + 相对路径拼接
  if (data.media_list) {
    data.media_list = data.media_list.map((m: any) => {
      const fileUrl = m.file_url || m.photo_file || ''
      return {
        ...m,
        id: m.id || String(m.photo_seq || ''),
        file_url:
          fileUrl && fileUrl.startsWith('/')
            ? MEDIA_BASE_URL + fileUrl
            : fileUrl
      }
    })
  }

  // result: 从 result_history 最新条目补全缺失字段
  const latestHistory =
    data.result_history && data.result_history.length > 0
      ? data.result_history[0]
      : null
  const firstMedia =
    data.media_list && data.media_list.length > 0 ? data.media_list[0] : null

  if (data.result) {
    // 从 result_history 补全分析结果字段
    if (latestHistory) {
      if (!data.result.tag_value)
        data.result.tag_value = latestHistory.tag_value
      if (!data.result.reason) data.result.reason = latestHistory.reason
      if (!data.result.label_info)
        data.result.label_info = latestHistory.label_info
      if (!data.result.image_count && latestHistory.image_count !== undefined)
        data.result.image_count = latestHistory.image_count
      if (!data.result.video_count && latestHistory.video_count !== undefined)
        data.result.video_count = latestHistory.video_count
    }
    // 从顶层或 media_list 补全基本信息字段
    if (!data.result.checkpoint_name)
      data.result.checkpoint_name =
        data.checkpoint_name || firstMedia?.checkpoint_name || ''
    if (!data.result.machine_id)
      data.result.machine_id = firstMedia?.machine_id || ''
    if (!data.result.machine_model)
      data.result.machine_model = firstMedia?.machine_model || ''
    if (!data.result.process_name)
      data.result.process_name = firstMedia?.process_name || ''
    // created_at → analyzed_at 兼容
    if (!data.result.analyzed_at)
      data.result.analyzed_at = data.result.created_at || ''
  } else if (latestHistory) {
    // result 为 null 但 result_history 有数据时，用最新历史构建 result
    data.result = {
      ...latestHistory,
      checkpoint_name:
        data.checkpoint_name || firstMedia?.checkpoint_name || '',
      machine_id: firstMedia?.machine_id || '',
      machine_model: firstMedia?.machine_model || '',
      process_name: firstMedia?.process_name || '',
      analyzed_at: latestHistory.created_at || ''
    }
  }

  // tag_value 中文→英文映射
  if (data.result && data.result.tag_value) {
    data.result.tag_value = mapTagValue(data.result.tag_value)
  }

  // label_info: 解析 JSON 字符串 + is_compliant → passed 映射
  if (data.result) {
    let labelInfo = data.result.label_info
    if (labelInfo && typeof labelInfo === 'string') {
      try {
        labelInfo = JSON.parse(labelInfo)
      } catch (e) {
        labelInfo = null
      }
    }
    if (labelInfo && labelInfo.rules) {
      labelInfo.rules = labelInfo.rules.map((rule: any) => ({
        ...rule,
        passed:
          rule.is_compliant !== undefined ? rule.is_compliant : rule.passed
      }))
    }
    data.result.label_info = labelInfo
  }

  return data
}

// ============================================================
// 审批操作
// ============================================================

/** 审批通过 */
export async function apiCheckpointApprove(
  dispatch: (...args: any[]) => any,
  params: { checkpoint_key: string; operator_id?: string; comment?: string }
) {
  return callWorkOrderApi(dispatch, 'checkpoint_approve', params)
}

/** 审批驳回 */
export async function apiCheckpointReject(
  dispatch: (...args: any[]) => any,
  params: { checkpoint_key: string; operator_id?: string; comment: string }
) {
  return callWorkOrderApi(dispatch, 'checkpoint_reject', params)
}

/** 发起复议 */
export async function apiCheckpointAppeal(
  dispatch: (...args: any[]) => any,
  params: { checkpoint_key: string; operator_id?: string; comment: string }
) {
  return callWorkOrderApi(dispatch, 'checkpoint_appeal', params)
}

/** 复议通过 */
export async function apiCheckpointAppealApprove(
  dispatch: (...args: any[]) => any,
  params: { checkpoint_key: string; operator_id?: string; comment?: string }
) {
  return callWorkOrderApi(dispatch, 'checkpoint_appeal_approve', params)
}

/** 复议驳回 */
export async function apiCheckpointAppealReject(
  dispatch: (...args: any[]) => any,
  params: { checkpoint_key: string; operator_id?: string; comment: string }
) {
  return callWorkOrderApi(dispatch, 'checkpoint_appeal_reject', params)
}

/** 提交整改（带媒体） */
export async function apiCheckpointRectify(
  dispatch: (...args: any[]) => any,
  params: {
    checkpoint_key: string
    operator_id?: string
    comment?: string
    new_media?: Array<{ url: string; media_type: number }>
    deleted_media_ids?: string[]
  }
) {
  return callWorkOrderApi(dispatch, 'checkpoint_rectify_with_media', params)
}

/** 撤销通过 */
export async function apiCheckpointRevoke(
  dispatch: (...args: any[]) => any,
  params: { checkpoint_key: string; operator_id?: string; comment: string }
) {
  return callWorkOrderApi(dispatch, 'checkpoint_revoke', params)
}

/** 删除工单 */
export async function apiCheckpointDeleteOrder(
  dispatch: (...args: any[]) => any,
  params: { checkpoint_key: string; operator_id?: string }
) {
  return callWorkOrderApi(dispatch, 'checkpoint_delete_order', params)
}

/** 获取用户权限列表 */
export async function apiUserPermissions(
  dispatch: (...args: any[]) => any,
  params: { user_id: string }
) {
  return callWorkOrderApi(dispatch, 'user_permissions', params)
}

// ============================================================
// 操作类型 → API 函数映射（供 slice 中的通用 action thunk 使用）
// ============================================================

export const ACTION_API_MAP: Record<
  string,
  (dispatch: (...args: any[]) => any, params: any) => Promise<any>
> = {
  approve: apiCheckpointApprove,
  reject: apiCheckpointReject,
  appeal: apiCheckpointAppeal,
  appeal_approve: apiCheckpointAppealApprove,
  appeal_reject: apiCheckpointAppealReject,
  submit_rectify: apiCheckpointRectify,
  resubmit: apiCheckpointRectify,
  revoke: apiCheckpointRevoke,
  delete_order: apiCheckpointDeleteOrder
}

const COS_BUCKET = 'net-cloud-1322834237'
const COS_REGION = 'ap-guangzhou'
const COS_URL_PREFIX = 'https://net-cloud.xhey.top/'

function generateCosKey(file: File): string {
  const ext = file.name.split('.').pop() || 'jpg'
  const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
  return `group/photo/${uuid}.${ext}`
}

/**
 * 上传单个文件到腾讯云 COS
 * @returns 完整的公网 URL
 */
function uploadFileToCOS(file: File): Promise<string> {
  const key = generateCosKey(file)
  return new Promise((resolve, reject) => {
    cos.uploadFile(
      {
        Bucket: COS_BUCKET,
        Region: COS_REGION,
        Key: key,
        Body: file,
        SliceSize: 1024 * 1024 * 30
      },
      (err: any) => {
        if (err) {
          reject(err)
        } else {
          resolve(COS_URL_PREFIX + key)
        }
      }
    )
  })
}

/**
 * 批量上传文件到 COS
 * @returns [{ url, media_type }]
 */
export async function uploadFilesToCOS(
  files: Array<{ file: File; isVideo: boolean }>
): Promise<Array<{ url: string; media_type: number }>> {
  const results: Array<{ url: string; media_type: number }> = []
  for (const item of files) {
    const url = await uploadFileToCOS(item.file)
    results.push({ url, media_type: item.isVideo ? 1 : 0 })
  }
  return results
}

// ============================================================
// 模拟拍照
// ============================================================

/** 模拟拍照上传 */
export async function apiSimulatePhoto(
  dispatch: (...args: any[]) => any,
  params: {
    group_id: string
    file_url: string
    media_type: number
    user_id?: string
  }
) {
  return callWorkOrderApi(dispatch, 'simulate_photo', params)
}

/** 活跃点位追踪 */
export async function apiGetActiveCheckpoints(
  dispatch: (...args: any[]) => any,
  params: { group_id: string }
) {
  const data = await callWorkOrderApi<{ checkpoints?: any[] }>(
    dispatch,
    'get_active_checkpoints',
    params
  )

  // 后端 stage 可能返回中文 tag_value（如 '符合'/'不符合'/'待分析'），需映射为英文
  const stageMap: Record<string, string> = {
    ...TAG_VALUE_API_MAP, // 符合→compliant, 不符合→non_compliant, 待分析→pending
    收集中: 'collecting',
    分析中: 'analyzing',
    整改中: 'rectifying',
    审核中: 'reviewing',
    已驳回: 'rejected',
    复议中: 'appeal_reviewing',
    已完成: 'approved'
  }

  if (data?.checkpoints) {
    data.checkpoints = data.checkpoints.map((cp: any) => ({
      ...cp,
      stage: stageMap[cp.stage] || cp.stage,
      tag_value: cp.tag_value ? mapTagValue(cp.tag_value) : cp.tag_value
    }))
  }

  return data
}

/** 轮询分析结果通知 */
export async function apiGetNewResults(
  dispatch: (...args: any[]) => any,
  params: { group_id: string; since?: string }
) {
  return callWorkOrderApi(dispatch, 'get_new_results', params)
}

// ============================================================
// 知识库
// ============================================================

/** 知识库列表 */
export async function apiKbList(
  dispatch: (...args: any[]) => any,
  params: {
    search?: string
    page?: number
    page_size?: number
    has_images?: boolean
    has_videos?: boolean
  }
) {
  return callWorkOrderApi(dispatch, 'kb_list', params)
}

/** 知识库详情 */
export async function apiKbDetail(
  dispatch: (...args: any[]) => any,
  params: { id: string }
) {
  return callWorkOrderApi(dispatch, 'kb_detail', params)
}

/** 保存/新建知识库条目 */
export async function apiKbSave(
  dispatch: (...args: any[]) => any,
  payload: any
) {
  return callWorkOrderApi(dispatch, 'kb_save', payload)
}

/** 刷新知识库缓存 */
export async function apiKbRefresh(dispatch: (...args: any[]) => any) {
  return callWorkOrderApi(dispatch, 'kb_refresh', {})
}

/** 删除知识库条目 */
export async function apiKbDelete(
  dispatch: (...args: any[]) => any,
  params: { id: string }
) {
  return callWorkOrderApi(dispatch, 'kb_delete', params)
}

/** 重新打开工单 */
export async function apiOrderReopen(
  dispatch: (...args: any[]) => any,
  params: { checkpoint_key: string; operator_id?: string; comment?: string }
) {
  return callWorkOrderApi(dispatch, 'order_reopen', params)
}
